/**
 * Paywall hooks
 *
 * Single React Query cache for the public PaymentConfig snapshot. Drives
 * every PaywallGate / AIFeatureGate / route guard on the platform.
 *
 * Two config sources, deliberately kept under two query keys:
 *
 * - `["payment-config"]` — anonymous, shared, cacheable. Answers *"does this
 *   feature exist on this deployment?"*
 * - `["byok","status"]` — authenticated, per-user. Answers *"can **I** use it?"*
 *
 * Merging them into one key would let one user's key state be served to
 * another from a shared cache entry. They stay separate; only the resolved
 * answer is merged, per render, in `useAIFeatureEnabled`.
 */

import { useQuery } from "@tanstack/react-query";
import { Capacitor } from "@capacitor/core";
import { apiClient } from "@shared/lib/api/client";
import { useAuthSnapshot } from "@shared/lib/auth/use-auth-snapshot";
// The BYOK client layer owns the endpoint and its types. Importing the fetcher
// rather than re-declaring it keeps one definition of `/api/v1/ai-keys/status/`.
// Safe direction: `ai_services/frontend/api` imports only `apiClient`, so there
// is no cycle back into this module (`hooks/use-byok` is the one that would).
import { getByokStatus } from "@apps/ai_services/frontend/api";
import type { ByokStatus } from "@apps/ai_services/frontend/api";

export interface PaymentConfigPublic {
	paymentsEnabled: boolean;
	/**
	 * Whether users may bring their own provider key on this deployment
	 * (`settings.BYOK_ENABLED`). Unlocks **AI** surfaces only — never wallets,
	 * pricing or checkout.
	 */
	byokEnabled: boolean;
	aiFeaturesEnabled: Record<string, boolean>;
	defaultCurrency?: string;
	lowBalanceThresholdCents?: number;
	/**
	 * Active payment provider key per platform, e.g.
	 * `{ web: "stripe", ios: "apple_iap", android: "" }`.
	 * An empty string means payments are disabled on that platform — on iOS
	 * this is the case until the App Store IAP review is approved
	 * (`PaymentConfig.payments_enabled_native_ios`). Only present when
	 * `paymentsEnabled` is true.
	 */
	providerByPlatform?: {
		web?: string;
		ios?: string;
		android?: string;
	};
}

/**
 * Why a feature is or is not available to *this* user.
 *
 * - `"ok"` — render it.
 * - `"byok_key_required"` — the feature exists here; the user needs a key.
 *   A door, not a wall: show the unlock CTA, never a redirect.
 * - `"disabled"` — the feature does not exist on this deployment. Invisible.
 */
export type FeatureReason = "ok" | "byok_key_required" | "disabled";

export interface AIFeatureAvailability {
	enabled: boolean;
	isLoading: boolean;
	reason: FeatureReason;
}

export const paywallKeys = {
	all: ["payment-config"] as const,
};

/** Per-user BYOK status. Never merged into `paywallKeys` — see module docstring. */
export const byokStatusKeys = {
	status: ["byok", "status"] as const,
};

export function readBootstrappedConfig(): PaymentConfigPublic | null {
	if (typeof window === "undefined") return null;
	const bootstrap = (window as unknown as {
		__PAYMENT_CONFIG__?: PaymentConfigPublic;
	}).__PAYMENT_CONFIG__;
	return bootstrap ?? null;
}

const SAFE_DEFAULT: PaymentConfigPublic = {
	paymentsEnabled: false,
	byokEnabled: false,
	aiFeaturesEnabled: {},
	defaultCurrency: "USD",
	lowBalanceThresholdCents: 100,
};

export function usePaymentConfig() {
	return useQuery<PaymentConfigPublic>({
		queryKey: paywallKeys.all,
		queryFn: async () => {
			const payload = await apiClient.get<Partial<PaymentConfigPublic>>(
				"/api/payment/config/public/",
				{
				auth: false,
				},
			);
			// `aiFeaturesEnabled` means "this feature exists here". Either kill
			// switch being on is enough to publish it; with both off the platform
			// genuinely has no AI features and must look like it.
			const available = Boolean(payload.paymentsEnabled || payload.byokEnabled);
			return {
				...SAFE_DEFAULT,
				...payload,
				byokEnabled: Boolean(payload.byokEnabled),
				aiFeaturesEnabled: available ? (payload.aiFeaturesEnabled ?? {}) : {},
			};
		},
		initialData: readBootstrappedConfig() ?? undefined,
		staleTime: 0,
		gcTime: 5 * 60_000,
		refetchOnWindowFocus: true,
		retry: 1,
	});
}

/**
 * The authenticated per-user answer. Only fetched when it can change an
 * outcome: an anonymous visitor has no key to check, and a deployment with
 * BYOK off has nowhere to put one.
 */
function useByokStatusSnapshot(enabled: boolean) {
	return useQuery<ByokStatus>({
		queryKey: byokStatusKeys.status,
		queryFn: getByokStatus,
		enabled,
		staleTime: 0,
		retry: 0,
	});
}

export function usePaywall(): { enabled: boolean; isLoading: boolean } {
	const { data, isLoading } = usePaymentConfig();
	return {
		enabled: Boolean(data?.paymentsEnabled),
		isLoading: isLoading && data === undefined,
	};
}

/**
 * Whether this deployment lets users bring their own provider key.
 *
 * Deployment-level only — it says nothing about whether *this* user has one.
 * Used by the nav and the landing page, which must show an AI feature exists
 * before the user can learn it needs a key.
 */
export function useByokEnabled(): { enabled: boolean; isLoading: boolean } {
	const { data, isLoading } = usePaymentConfig();
	return {
		enabled: Boolean(data?.byokEnabled),
		isLoading: isLoading && data === undefined,
	};
}

/**
 * Resolve one AI feature against both config paths.
 *
 * `enabled` keeps exactly the meaning it has always had — payments on plus the
 * per-feature flag, or a working user key — so the ~12 existing call sites need
 * no edit. `reason` is additive, and is what lets a gate tell "you need a key"
 * apart from "this does not exist here".
 */
export function useAIFeatureEnabled(featureKey: string): AIFeatureAvailability {
	const { data, isLoading } = usePaymentConfig();
	const { isAuthenticated } = useAuthSnapshot();
	const resolved = data ?? SAFE_DEFAULT;
	const configLoading = isLoading && data === undefined;

	const featureExists = Boolean(resolved.aiFeaturesEnabled?.[featureKey]);
	const platformEnabled = Boolean(resolved.paymentsEnabled && featureExists);

	// The payment path can only *grant*; the per-user status can only *add* to
	// it. So when payments already unlock the feature we answer immediately
	// rather than blanking the surface until an authenticated round-trip lands.
	const consultStatus =
		!platformEnabled && isAuthenticated && Boolean(resolved.byokEnabled);

	const {
		data: status,
		isLoading: statusLoading,
		isError: statusError,
	} = useByokStatusSnapshot(consultStatus);

	if (platformEnabled) {
		return { enabled: true, isLoading: configLoading, reason: "ok" };
	}

	// Fail closed while the deployment shape is still unknown.
	if (configLoading) {
		return { enabled: false, isLoading: true, reason: "disabled" };
	}

	const lockedReason: FeatureReason =
		resolved.byokEnabled && featureExists ? "byok_key_required" : "disabled";

	if (!consultStatus) {
		return { enabled: false, isLoading: false, reason: lockedReason };
	}

	// Don't flash the unlock CTA at a user who already has a working key.
	if (statusLoading || (status === undefined && !statusError)) {
		return { enabled: false, isLoading: true, reason: "disabled" };
	}

	const featureStatus = status?.features?.[featureKey];
	if (featureStatus?.available) {
		return { enabled: true, isLoading: false, reason: "ok" };
	}
	if (featureStatus?.reason === "byok_key_required") {
		return { enabled: false, isLoading: false, reason: "byok_key_required" };
	}
	// Unknown feature, `system_only`, `unavailable`, or a failed status call:
	// fall back to what the anonymous config alone can prove.
	return {
		enabled: false,
		isLoading: false,
		reason: featureStatus ? "disabled" : lockedReason,
	};
}

/**
 * Which providers could serve a given feature, for the unlock CTA.
 *
 * "Add a key" is a useless instruction without "…for one of these". The list
 * is the server's own capability match, so it never suggests a provider that
 * physically cannot do the job (no vision model, no speech-to-text, …).
 */
export function useByokFeatureProviders(featureKey: string): {
	providers: string[];
	isLoading: boolean;
} {
	const { data: config } = usePaymentConfig();
	const { isAuthenticated } = useAuthSnapshot();
	const consultStatus = isAuthenticated && Boolean(config?.byokEnabled);
	const { data, isLoading } = useByokStatusSnapshot(consultStatus);
	if (!consultStatus) return { providers: [], isLoading: false };
	return {
		providers: data?.features?.[featureKey]?.providers ?? [],
		isLoading,
	};
}

/**
 * Whether *purchase* surfaces (pricing, checkout, "Top up") may be shown on
 * the current platform.
 *
 * - **Web**: gated only by the global `paymentsEnabled` flag.
 * - **iOS / Android**: additionally requires a payment provider to be active
 *   for that platform. On iOS, Apple requires in-app purchases for digital
 *   goods (Guideline 3.1.1); until the StoreKit IAP product is approved,
 *   `PaymentConfig.payments_enabled_native_ios` stays false → the public
 *   config returns an empty `providerByPlatform.ios`, and every purchase
 *   surface must be hidden to avoid an App Store rejection.
 *
 * BYOK does not enter into this. A user on their own key is not buying
 * anything, and a free deployment has nothing to sell.
 *
 * Balance display and read-only wallet history are NOT gated by this hook —
 * only the buy/top-up call-to-action surfaces are.
 */
export function useNativePaymentsEnabled(): {
	enabled: boolean;
	isLoading: boolean;
} {
	const { data, isLoading } = usePaymentConfig();
	const resolved = data ?? SAFE_DEFAULT;
	const platform = Capacitor.getPlatform();

	let enabled = Boolean(resolved.paymentsEnabled);
	if (enabled && (platform === "ios" || platform === "android")) {
		const provider = resolved.providerByPlatform?.[platform];
		enabled = Boolean(provider);
	}

	return { enabled, isLoading: isLoading && data === undefined };
}
